import { Component, OnDestroy, OnInit, ChangeDetectionStrategy, computed, effect, inject, output, input, signal } from '@angular/core';
import {Subject} from 'rxjs';
import {take, takeUntil} from 'rxjs/operators';
import {
  DEFAULT_PASSWORD_POLICY,
  PasswordPolicy,
  PasswordPolicyService
} from '@app/services/password-policy.service';
import { MatProgressBar } from '@angular/material/progress-bar';
import { MatIcon } from '@angular/material/icon';

interface PasswordRequirement {
  key: string;
  message: string;
  isValid: boolean;
}

@Component({
  selector: 'app-password-validation',
  templateUrl: './password-validation.component.html',
  styleUrls: ['./password-validation.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatProgressBar, MatIcon]
})
export class PasswordValidationComponent implements OnInit, OnDestroy {
  private passwordPolicyService = inject(PasswordPolicyService);

  readonly password = input('');
  readonly email = input('');
  readonly username = input('');
  readonly firstName = input('');
  readonly lastName = input('');
  readonly company = input('');
  /**
   * The rules to check against. Pass one when the host already holds a policy - a
   * screen that fetched the effective policy for something else, or a test - and this
   * component makes no request of its own. Leave it unset and it reads the shared
   * service, which fetches once for the whole application.
   */
  readonly policy = input<PasswordPolicy | null>(null);
  // eslint-disable-next-line @angular-eslint/no-output-on-prefix -- renaming the output would change every binding to it
  readonly onStrengthChanged = output<number>();

  /** The service's policy once it answers; the shipped defaults stand in until then. */
  private readonly sharedPolicy = signal<PasswordPolicy>({...DEFAULT_PASSWORD_POLICY});
  private readonly policyInUse = computed<PasswordPolicy>(() => {
    const policy = this.policy();
    return policy ? {...DEFAULT_PASSWORD_POLICY, ...policy} : this.sharedPolicy();
  });
  readonly requirements = computed(() => this.validatePassword());
  readonly progress = computed(() => {
    const requirements = this.requirements();
    const fulfilled = requirements.filter((requirement) => requirement.isValid).length;
    return requirements.length ? Math.round((fulfilled / requirements.length) * 100) : 100;
  });
  private readonly unsubscriber = new Subject<void>();

  constructor() {
    // The host hears the strength each time the password, the person or the policy changes.
    effect(() => {
      this.requirements();
      this.onStrengthChanged.emit(this.progress());
    });
  }

  private get activePolicy(): PasswordPolicy {
    return this.policyInUse();
  }

  ngOnInit(): void {
    if (this.policy()) {
      return;
    }
    // Never errors: the service answers with the shipped defaults when the endpoint
    // cannot be reached, so the hints degrade rather than the form breaking.
    this.passwordPolicyService.getPolicy()
      .pipe(take(1), takeUntil(this.unsubscriber))
      .subscribe((policy) => this.sharedPolicy.set(policy));
  }

  ngOnDestroy(): void {
    this.unsubscriber.next();
    this.unsubscriber.complete();
  }

  private validatePassword(): PasswordRequirement[] {
    const normalized = String(this.password() || '').normalize('NFC');
    const normalizedCandidate = this.normalizeForComparison(normalized);
    const folded = normalized.toLocaleLowerCase('en-US');
    const requirements: PasswordRequirement[] = [];
    const length = Array.from(normalized).length;

    requirements.push({
      key: 'length',
      message: `contains between ${this.activePolicy.minLength} and ${this.activePolicy.maxLength} characters`,
      isValid: length >= this.activePolicy.minLength && length <= this.activePolicy.maxLength
    });
    if (this.activePolicy.requireLowercase) {
      requirements.push({
        key: 'lowercase',
        message: 'contains at least one lowercase letter',
        isValid: /[a-z]/.test(normalized)
      });
    }
    if (this.activePolicy.requireUppercase) {
      requirements.push({
        key: 'uppercase',
        message: 'contains at least one uppercase letter',
        isValid: /[A-Z]/.test(normalized)
      });
    }
    if (this.activePolicy.requireNumber) {
      requirements.push({
        key: 'number',
        message: 'contains at least one number',
        isValid: /[0-9]/.test(normalized)
      });
    }
    if (this.activePolicy.requireSpecialCharacter) {
      requirements.push({
        key: 'special',
        message: `contains at least one special character (${this.activePolicy.specialCharacters})`,
        isValid: Array.from(normalized)
          .some((character) => this.activePolicy.specialCharacters.includes(character))
      });
    }

    const contextParts = this.getContextParts();
    requirements.push({
      key: 'context',
      message: 'does not contain your email, name, company, or restricted product/domain terms',
      isValid: !contextParts.some((part) => normalizedCandidate.includes(part))
    });
    requirements.push({
      key: 'common',
      message: 'is not a commonly used password',
      isValid: !this.activePolicy.commonPasswords.some((candidate) => {
        const normalizedCommonPassword = String(candidate).toLocaleLowerCase('en-US');
        return folded === normalizedCommonPassword
          || normalizedCandidate === this.normalizeForComparison(candidate);
      })
    });
    requirements.push({
      key: 'sequence',
      message: 'does not contain predictable sequences or repeated characters',
      isValid: !this.hasSequence(folded)
    });

    return requirements;
  }

  trackRequirement(_index: number, requirement: PasswordRequirement): string {
    return requirement.key;
  }

  private getContextParts(): string[] {
    const emailIdentity = String(this.email() || '').split('@')[0];
    const identityValues = [
      emailIdentity,
      this.email(),
      this.username(),
      this.firstName(),
      this.lastName(),
      this.company()
    ];
    const identityParts = identityValues
      .filter(Boolean)
      .reduce((parts: string[], value) => [
        ...parts,
        value,
        ...String(value).split(/[^\p{L}\p{N}]+/u)
      ], [])
      .map((value) => this.normalizeForComparison(value));
    const deniedParts = (this.activePolicy.contextDenylist || [])
      .map((value) => this.normalizeForComparison(value));

    return [...identityParts, ...deniedParts]
      .filter((value) => Array.from(value).length >= this.activePolicy.contextMinimumCharacters);
  }

  private normalizeForComparison(value: string): string {
    const deLeeted = String(value || '').normalize('NFC')
      .normalize('NFKD')
      .replace(/\p{M}/gu, '')
      .toLocaleLowerCase('en-US')
      .replace(/[4@301$!57]/g, (character) => ({
        4: 'a',
        '@': 'a',
        3: 'e',
        0: 'o',
        1: 'i',
        '!': 'i',
        $: 's',
        5: 's',
        7: 't'
      })[character]);
    return deLeeted.replace(/[^\p{L}\p{N}]/gu, '');
  }

  private hasSequence(value: string): boolean {
    const length = this.activePolicy.sequenceLength;
    if (new RegExp(`(.)\\1{${length - 1},}`, 'u').test(value)) {
      return true;
    }
    // Kept in step with hasSequence in the server's Password.service: the alphabet, the
    // digits, the three keyboard rows and the columns a hand walks down (1qaz, 2wsx).
    // A hint that misses what the server refuses is worse than no hint.
    return [
      'abcdefghijklmnopqrstuvwxyz', '0123456789',
      'qwertyuiop', 'asdfghjkl', 'zxcvbnm',
      '1qaz', '2wsx', '3edc', '4rfv', '5tgb', '6yhn', '7ujm', '8ik', '9ol'
    ]
      .some((sequence) => {
        // A column is shorter than the policy length; walking one whole still counts.
        const window = Math.min(length, sequence.length);
        for (let index = 0; index <= sequence.length - window; index += 1) {
          const part = sequence.slice(index, index + window);
          if (value.includes(part) || value.includes(Array.from(part).reverse().join(''))) {
            return true;
          }
        }
        return false;
      });
  }
}
